import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { tripsApi } from '../services/api';

// Puss in Boots style cat SVG - Çizmeli Kedi Miv
const MivCatIcon = ({ className = "", isOpen = false }) => (
  <svg 
    viewBox="0 0 100 100" 
    className={className}
    style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' }}
  >
    {/* Hat */}
    <ellipse cx="50" cy="18" rx="22" ry="6" fill="#8B4513" />
    <path d="M28 18 Q30 5 50 8 Q70 5 72 18" fill="#A0522D" />
    <path d="M30 18 Q32 8 50 10 Q68 8 70 18" fill="#8B4513" />
    
    {/* Hat band */}
    <rect x="28" y="15" width="44" height="4" fill="#FFD700" rx="1" />
    
    {/* Feather */}
    <path d="M65 8 Q80 -5 75 15 Q72 10 70 12" fill="#22C55E" stroke="#16A34A" strokeWidth="0.5" />
    <path d="M68 5 Q75 0 73 12" fill="none" stroke="#16A34A" strokeWidth="1" />
    
    {/* Left ear */}
    <path d="M22 28 L18 45 L32 38 Z" fill="#FF9F43" />
    <path d="M24 32 L22 42 L30 38 Z" fill="#FFB6C1" />
    
    {/* Right ear */}
    <path d="M78 28 L82 45 L68 38 Z" fill="#FF9F43" />
    <path d="M76 32 L78 42 L70 38 Z" fill="#FFB6C1" />
    
    {/* Cat head */}
    <ellipse cx="50" cy="50" rx="28" ry="24" fill="#FF9F43" />
    
    {/* Face lighter area */}
    <ellipse cx="50" cy="52" rx="20" ry="16" fill="#FFDAB3" />
    
    {/* Big cute eyes - Puss in Boots style */}
    <ellipse cx="38" cy="48" rx="10" ry="11" fill="white" />
    <ellipse cx="62" cy="48" rx="10" ry="11" fill="white" />
    
    {/* Pupils - big and cute */}
    <circle cx={isOpen ? "40" : "38"} cy={isOpen ? "50" : "48"} r="7" fill="#2D3748" />
    <circle cx={isOpen ? "64" : "62"} cy={isOpen ? "50" : "48"} r="7" fill="#2D3748" />
    
    {/* Eye shine */}
    <circle cx={isOpen ? "42" : "40"} cy="46" r="2.5" fill="white" />
    <circle cx={isOpen ? "66" : "64"} cy="46" r="2.5" fill="white" />
    <circle cx={isOpen ? "38" : "36"} cy="50" r="1" fill="white" />
    <circle cx={isOpen ? "62" : "60"} cy="50" r="1" fill="white" />
    
    {/* Eyebrows - expressive */}
    <path d="M28 38 Q33 35 42 38" fill="none" stroke="#8B4513" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M58 38 Q67 35 72 38" fill="none" stroke="#8B4513" strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Nose */}
    <path d="M47 58 L50 62 L53 58 Z" fill="#FF6B6B" />
    
    {/* Mouth - charming smile */}
    <path d="M50 62 Q50 66 50 64" fill="none" stroke="#5D4037" strokeWidth="1" />
    <path d="M50 64 Q44 70 40 66" fill="none" stroke="#5D4037" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M50 64 Q56 70 60 66" fill="none" stroke="#5D4037" strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Whiskers */}
    <line x1="30" y1="55" x2="18" y2="52" stroke="#5D4037" strokeWidth="1" />
    <line x1="30" y1="60" x2="16" y2="60" stroke="#5D4037" strokeWidth="1" />
    <line x1="30" y1="65" x2="18" y2="68" stroke="#5D4037" strokeWidth="1" />
    <line x1="70" y1="55" x2="82" y2="52" stroke="#5D4037" strokeWidth="1" />
    <line x1="70" y1="60" x2="84" y2="60" stroke="#5D4037" strokeWidth="1" />
    <line x1="70" y1="65" x2="82" y2="68" stroke="#5D4037" strokeWidth="1" />
    
    {/* Cape/Collar */}
    <path d="M25 72 Q50 78 75 72 Q78 80 70 85 Q50 90 30 85 Q22 80 25 72" fill="#22C55E" />
    <path d="M28 74 Q50 79 72 74" fill="none" stroke="#16A34A" strokeWidth="2" />
    
    {/* Gold clasp */}
    <circle cx="50" cy="75" r="4" fill="#FFD700" stroke="#DAA520" strokeWidth="1" />
    <circle cx="50" cy="75" r="2" fill="#FFF8DC" />
    
    {/* Boots hint at bottom */}
    <ellipse cx="35" cy="95" rx="8" ry="5" fill="#8B4513" />
    <ellipse cx="65" cy="95" rx="8" ry="5" fill="#8B4513" />
    <ellipse cx="35" cy="93" rx="6" ry="2" fill="#A0522D" />
    <ellipse cx="65" cy="93" rx="6" ry="2" fill="#A0522D" />
  </svg>
);

const MivChatbot = () => {
  const { darkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const bgMain = darkMode ? 'bg-slate-900' : 'bg-white';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-gray-50';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  const exampleQuestions = [
    { icon: '🕐', text: "Giresun'da 3 saatim var, tarih ve yemek istiyorum. Hızlı plan yap." },
    { icon: '🌲', text: "Doğa severler için günübirlik rota öner" },
    { icon: '🍽️', text: "En iyi yerel restoranları söyle" },
    { icon: '🏛️', text: "Tarihi mekanları anlat" },
    { icon: '☕', text: "Kahve molası için nereye gideyim?" },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleFeedback = async (messageId, rating) => {
    if (!messageId) return;
    
    try {
      await tripsApi.mivFeedback(messageId, rating);
      // Update message feedback state
      setMessages(prev => prev.map(msg => 
        msg.messageId === messageId 
          ? { ...msg, feedback: rating }
          : msg
      ));
    } catch (error) {
      console.error('Feedback error:', error);
    }
  };

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await tripsApi.mivChat(text, sessionId);
      
      if (response.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.message,
          messageId: response.message_id,
          feedback: null
        }]);
        if (response.session_id) {
          setSessionId(response.session_id);
        }
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.message || 'Miyav! Bir sorun oluştu 😿',
          messageId: null,
          feedback: null
        }]);
      }
    } catch (error) {
      console.error('Miv chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Miyav! Bağlantı sorunu var, tekrar dener misin? 😿',
        messageId: null,
        feedback: null
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (text) => {
    handleSendMessage(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Cat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-4 z-50 w-16 h-16 rounded-full shadow-xl transition-all duration-300 hover:scale-110 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          animation: !isOpen ? 'mivBounce 2s ease-in-out infinite' : 'none',
        }}
      >
        <MivCatIcon className="w-full h-full" isOpen={false} />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-24 right-4 z-50 w-80 sm:w-96 transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        style={{ transformOrigin: 'bottom right' }}
      >
        <div className={`${bgMain} rounded-2xl shadow-2xl border ${borderColor} overflow-hidden flex flex-col`} style={{ height: '500px' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-full p-1 flex-shrink-0">
              <MivCatIcon className="w-full h-full" isOpen={true} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white">Miv</h3>
              <p className="text-sm text-green-100">Seyahat Asistanı 🐱</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <>
                {/* Welcome Message */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex-shrink-0 p-0.5">
                    <MivCatIcon className="w-full h-full" />
                  </div>
                  <div className={`${bgCard} rounded-2xl rounded-tl-sm p-3 max-w-[80%]`}>
                    <p className={textPrimary}>
                      Merhaba! Ben Miv 👋<br />
                      Giresun'u keşfetmene yardımcı olabilirim!
                    </p>
                  </div>
                </div>

                {/* Example Questions */}
                <div className="space-y-2 mt-4">
                  <p className={`text-xs ${textSecondary} px-2`}>Örnek sorular:</p>
                  {exampleQuestions.map((q, index) => (
                    <button
                      key={index}
                      onClick={() => handleExampleClick(q.text)}
                      className={`w-full text-left p-3 ${bgCard} rounded-xl hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors border ${borderColor}`}
                    >
                      <span className="mr-2">{q.icon}</span>
                      <span className={`text-sm ${textPrimary}`}>{q.text}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-green-100 flex-shrink-0 p-0.5">
                        <MivCatIcon className="w-full h-full" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <div
                        className={`rounded-2xl p-3 max-w-[80%] ${
                          msg.role === 'user'
                            ? 'bg-green-500 text-white rounded-tr-sm'
                            : `${bgCard} ${textPrimary} rounded-tl-sm`
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                      </div>
                      
                      {/* Feedback buttons for assistant messages */}
                      {msg.role === 'assistant' && msg.messageId && (
                        <div className="flex items-center gap-1 mt-1.5 ml-1">
                          {msg.feedback ? (
                            <div className={`flex items-center gap-1 text-xs ${textSecondary}`}>
                              <Check className="w-3 h-3 text-green-500" />
                              <span>Geri bildiriminiz kaydedildi</span>
                            </div>
                          ) : (
                            <>
                              <span className={`text-xs ${textSecondary} mr-1`}>Bu cevap yardımcı oldu mu?</span>
                              <button
                                onClick={() => handleFeedback(msg.messageId, 'like')}
                                className={`p-1.5 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors group`}
                                title="Beğen"
                              >
                                <ThumbsUp className={`w-4 h-4 ${textSecondary} group-hover:text-green-500`} />
                              </button>
                              <button
                                onClick={() => handleFeedback(msg.messageId, 'dislike')}
                                className={`p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors group`}
                                title="Beğenme"
                              >
                                <ThumbsDown className={`w-4 h-4 ${textSecondary} group-hover:text-red-500`} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex-shrink-0 p-0.5">
                      <MivCatIcon className="w-full h-full" />
                    </div>
                    <div className={`${bgCard} rounded-2xl rounded-tl-sm p-3`}>
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                        <span className={`text-sm ${textSecondary}`}>Miv düşünüyor...</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`p-3 border-t ${borderColor}`}>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Mesajınızı yazın..."
                className={`flex-1 px-4 py-2.5 rounded-full border ${borderColor} ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-green-500/50`}
                disabled={isLoading}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes mivBounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
      `}</style>
    </>
  );
};

export default MivChatbot;
